import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bot, Save, RotateCcw, Check, Loader2, LogOut, User } from "lucide-react";
import { KnowledgeBaseManager } from "@/components/KnowledgeBaseManager";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { signOut } from "@/lib/auth-client";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export function SettingsPage() {
  const [view, setView] = useState<"persona" | "kb" | "ai" | "account">("persona");
  const [prompt, setPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");
  const [me, setMe] = useState<{ email: string | null; isAdmin: boolean } | null>(null);
  const [globalAI, setGlobalAI] = useState<{ enabled: boolean; updatedAt: number | null; updatedBy: string | null } | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isSavingAI, setIsSavingAI] = useState(false);
  const [aiError, setAiError] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifOnlyHidden, setNotifOnlyHidden] = useState(true);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | "unsupported">("default");

  useEffect(() => {
    loadPrompt();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const supported = "Notification" in window;
    setNotifPermission(supported ? Notification.permission : "unsupported");
    setNotifEnabled(window.localStorage.getItem("et.notifications.enabled") === "true");
    setNotifOnlyHidden(window.localStorage.getItem("et.notifications.onlyWhenHidden") !== "false");
  }, []);

  useEffect(() => {
    if (view !== "ai") return;
    void loadAIControls();
  }, [view]);

  // ... (existing functions: loadPrompt, loadAIControls, toggleGlobalAI, handleSave, handleReset)

  const loadPrompt = async () => {
    try {
      const { prompt } = await api.getPrompt();
      setPrompt(prompt);
      setOriginalPrompt(prompt);
    } catch (error) {
      console.error("Failed to load prompt:", error);
      setError("Failed to load prompt. Please check your permissions.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadAIControls = async () => {
    setIsLoadingAI(true);
    setAiError("");
    try {
      const [meRes, aiRes] = await Promise.all([api.getMe(), api.getGlobalAI()]);
      setMe(meRes);
      setGlobalAI(aiRes);
    } catch (e) {
      console.error("Failed to load AI controls:", e);
      setAiError(e instanceof Error ? e.message : "Failed to load AI controls");
    } finally {
      setIsLoadingAI(false);
    }
  };

  const toggleGlobalAI = async () => {
    if (!me?.isAdmin) return;
    if (!globalAI) return;
    setIsSavingAI(true);
    setAiError("");
    try {
      const next = await api.setGlobalAI(!globalAI.enabled);
      setGlobalAI(next);
    } catch (e) {
      console.error("Failed to update global AI:", e);
      setAiError(e instanceof Error ? e.message : "Failed to update global AI");
    } finally {
      setIsSavingAI(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    setSaveSuccess(false);

    try {
      await api.updatePrompt(prompt);
      setOriginalPrompt(prompt);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save prompt:", error);
      setError("Failed to save prompt. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPrompt(originalPrompt);
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  const persistNotifPrefs = (next: { enabled: boolean; onlyWhenHidden: boolean }) => {
    window.localStorage.setItem("et.notifications.enabled", String(next.enabled));
    window.localStorage.setItem("et.notifications.onlyWhenHidden", String(next.onlyWhenHidden));
  };

  const requestNotifications = async () => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) {
      setNotifPermission("unsupported");
      alert("Notifications are not supported in this browser.");
      return;
    }

    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm !== "granted") return;

    setNotifEnabled(true);
    persistNotifPrefs({ enabled: true, onlyWhenHidden: notifOnlyHidden });
  };

  const hasChanges = prompt !== originalPrompt;

  return (
    <>
      <Header title="Settings" subtitle="Configure your AI agent" />

      <div className="p-6 max-w-4xl mx-auto space-y-6 animate-fade-in text-left">
        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
          <Button variant={view === "persona" ? "default" : "outline"} onClick={() => setView("persona")} className="whitespace-nowrap">
            Persona & Style
          </Button>
          <Button variant={view === "kb" ? "default" : "outline"} onClick={() => setView("kb")} className="whitespace-nowrap">
            Knowledge Base
          </Button>
          <Button variant={view === "ai" ? "default" : "outline"} onClick={() => setView("ai")} className="whitespace-nowrap">
            AI Controls
          </Button>
          <Button variant={view === "account" ? "default" : "outline"} onClick={() => setView("account")} className="whitespace-nowrap">
            Account
          </Button>
        </div>

        {/* AI Prompt Editor */}
        {view === "persona" && (
        <Card className="border-border/60 shadow-sm overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Persona & Style</CardTitle>
                <CardDescription className="mt-1">
                  Editable persona/style only. Business facts come from the Knowledge Base.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter your AI system prompt..."
                  className="min-h-[400px] font-mono text-sm leading-relaxed"
                />

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                    {error}
                  </div>
                )}

                {saveSuccess && (
                  <div className="text-sm text-success bg-success/10 p-3 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Prompt saved successfully!
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-border/60">
                  <div className="text-sm text-muted-foreground">
                    {hasChanges ? (
                      <span className="text-warning">Unsaved changes</span>
                    ) : (
                      <span>No changes</span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      disabled={!hasChanges || isSaving}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!hasChanges || isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {view === "kb" && <KnowledgeBaseManager />}

        {view === "ai" && (
          <Card className="border-border/60 shadow-sm overflow-hidden">
            <CardHeader>
              <CardTitle>Global AI Switch</CardTitle>
              <CardDescription>Admin-only kill switch. Overrides per-chat AI toggles.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingAI ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border/60 bg-card/50">
                    <div className="space-y-1">
                      <div className="font-medium">AI Enabled</div>
                      <div className="text-sm text-muted-foreground">
                        {globalAI?.updatedAt
                          ? `Updated ${new Date(globalAI.updatedAt).toLocaleString()}${globalAI.updatedBy ? ` by ${globalAI.updatedBy}` : ""}`
                          : "Default: enabled"}
                      </div>
                      {!me?.isAdmin && <div className="text-sm text-warning">Admin only</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-muted-foreground">Global</Label>
                      <Switch
                        checked={globalAI?.enabled ?? true}
                        onCheckedChange={() => void toggleGlobalAI()}
                        disabled={!me?.isAdmin || isSavingAI || !globalAI}
                      />
                    </div>
                  </div>

                  {aiError && (
                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                      {aiError}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {view === "account" && (
          <Card className="border-border/60 shadow-sm overflow-hidden">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Account</CardTitle>
                  <CardDescription>Manage your account settings.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg border border-border/60 bg-card/50 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium">Notifications</div>
                    <div className="text-sm text-muted-foreground">
                      Agent assistance + inbound/outbound messages.
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Permission: {notifPermission}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => void requestNotifications()}
                      disabled={notifPermission === "unsupported"}
                    >
                      Enable
                    </Button>
                    <Switch
                      checked={notifEnabled}
                      onCheckedChange={(checked) => {
                        setNotifEnabled(checked);
                        persistNotifPrefs({ enabled: checked, onlyWhenHidden: notifOnlyHidden });
                      }}
                      disabled={notifPermission !== "granted"}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium">Only when tab hidden</div>
                    <div className="text-sm text-muted-foreground">
                      Avoid noise while you’re actively using the dashboard.
                    </div>
                  </div>
                  <Switch
                    checked={notifOnlyHidden}
                    onCheckedChange={(checked) => {
                      setNotifOnlyHidden(checked);
                      persistNotifPrefs({ enabled: notifEnabled, onlyWhenHidden: checked });
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border border-border/60 bg-card/50">
                <div>
                  <div className="font-medium">Sign Out</div>
                  <div className="text-sm text-muted-foreground">
                    Sign out of your account on this device.
                  </div>
                </div>
                <Button variant="destructive" onClick={() => setShowLogoutConfirm(true)}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips Card */}
        {view === "persona" && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3">Tips for a Great AI Prompt</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Define the human role clearly (e.g., "You are Sara, a friendly admissions coordinator")</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Keep it style-focused; put schedule/pricing/policies in the Knowledge Base</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Set boundaries for what the AI should and shouldn't discuss</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Specify the tone (friendly, professional, casual) for responses</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Include instructions for when to escalate to a human</span>
              </li>
            </ul>
          </CardContent>
        </Card>
        )}
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        onOpenChange={setShowLogoutConfirm}
        title="Sign Out"
        description="Are you sure you want to sign out? You will need to log in again to access the dashboard."
        confirmText="Sign Out"
        onConfirm={handleSignOut}
        variant="destructive"
      />
    </>
  );
}
